import React, { useState, useEffect } from "react";
import AdminNav from "../components/admin/AdminNav";
import { PRICES as DEFAULT_PRICES } from "../data/content";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCw, Layers, Check } from "lucide-react";

export default function AdminPrices() {
  const [prices, setPrices] = useState(DEFAULT_PRICES);
  const [activeCatIndex, setActiveCatIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [showAddCat, setShowAddCat] = useState(false);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await api.get("/site/content");
      if (res.data?.prices && Array.isArray(res.data.prices) && res.data.prices.length > 0) {
        setPrices(res.data.prices);
      } else {
        setPrices(DEFAULT_PRICES);
      }
    } catch (err) {
      console.warn("Using default prices:", err);
      setPrices(DEFAULT_PRICES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleServiceChange = (catIdx, rowIdx, field, value) => {
    const updated = JSON.parse(JSON.stringify(prices));
    updated[catIdx].rows[rowIdx][field] = value;
    setPrices(updated);
  };

  const handleAddRow = (catIdx) => {
    const updated = JSON.parse(JSON.stringify(prices));
    updated[catIdx].rows.push({ service: "Новая услуга", price: "от 1 000 ₽" });
    setPrices(updated);
  };

  const handleDeleteRow = (catIdx, rowIdx) => {
    const updated = JSON.parse(JSON.stringify(prices));
    updated[catIdx].rows.splice(rowIdx, 1);
    setPrices(updated);
  };

  const handleAddCategory = () => {
    if (!newCatName.trim()) return;
    const updated = [...prices, { category: newCatName.trim(), rows: [] }];
    setPrices(updated);
    setNewCatName("");
    setShowAddCat(false);
    setActiveCatIndex(updated.length - 1);
    toast.success(`Категория «${newCatName.trim()}» добавлена`);
  };

  const handleDeleteCategory = (catIdx) => {
    if (prices.length <= 1) {
      toast.error("Должна остаться хотя бы одна категория");
      return;
    }
    const catName = prices[catIdx].category;
    if (window.confirm(`Удалить категорию «${catName}» и все её услуги?`)) {
      const updated = prices.filter((_, idx) => idx !== catIdx);
      setPrices(updated);
      setActiveCatIndex(0);
      toast.success(`Категория «${catName}» удалена`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", { prices });
      toast.success("Прайс-лист сайта успешно сохранён и обновлен!");
    } catch (err) {
      toast.error(formatApiError(err, "Ошибка при сохранении прайс-листа"));
    } finally {
      setSaving(false);
    }
  };

  const activeCategory = prices[activeCatIndex] || prices[0];

  return (
    <div className="min-h-screen bg-[#F5EFE6] text-[#1A0E07]">
      <AdminNav />

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b-2 border-[#4A1419]/20 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-5xl text-[#4A1419] font-bold">
              Прайс-лист сайта
            </h1>
            <p className="text-sm text-[#6E5549] mt-1">
              Редактируйте категории, названия услуг и цены, которые видят посетители на сайте.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchContent}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium border-2 border-[#4A1419]/30 hover:border-[#4A1419] bg-[#F5EFE6] flex items-center gap-2"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              <span>Обновить</span>
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 text-sm font-bold bg-[#4A1419] text-[#F5EFE6] border-2 border-[#4A1419] hover:bg-[#320D10] flex items-center gap-2 shadow-md active:scale-95 transition-all"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span>Сохранение...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Сохранить на сайте</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Categories Bar */}
        <div className="flex flex-wrap items-center gap-2 mb-8 bg-[#EFE6D8] p-2 border-2 border-[#4A1419]/20">
          {prices.map((cat, idx) => {
            const isActive = idx === activeCatIndex;
            return (
              <button
                key={idx}
                onClick={() => setActiveCatIndex(idx)}
                className={`px-4 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wider transition-all border-2 ${
                  isActive
                    ? "bg-[#4A1419] text-[#F5EFE6] border-[#4A1419]"
                    : "bg-[#F5EFE6] text-[#1A0E07] border-transparent hover:border-[#4A1419]/40"
                }`}
              >
                {cat.category} ({cat.rows?.length || 0})
              </button>
            );
          })}

          <button
            onClick={() => setShowAddCat(true)}
            className="px-3.5 py-2.5 text-xs md:text-sm font-bold text-[#4A1419] hover:bg-[#4A1419]/10 border-2 border-dashed border-[#4A1419]/40 flex items-center gap-1.5"
          >
            <Plus size={15} />
            <span>Новая категория</span>
          </button>
        </div>

        {/* Add Category Modal/Inline */}
        {showAddCat && (
          <div className="mb-6 p-4 bg-white border-2 border-[#C5A059] shadow-sm flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Название новой категории..."
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              className="flex-1 min-w-[240px] px-3 py-2 border-2 border-[#4A1419]/30 text-sm focus:outline-none focus:border-[#4A1419]"
            />
            <button
              onClick={handleAddCategory}
              className="px-4 py-2 bg-[#4A1419] text-[#F5EFE6] text-xs font-bold uppercase tracking-wider"
            >
              Добавить
            </button>
            <button
              onClick={() => { setShowAddCat(false); setNewCatName(""); }}
              className="px-3 py-2 text-xs text-[#6E5549] hover:text-[#1A0E07]"
            >
              Отмена
            </button>
          </div>
        )}

        {/* Services Table in Selected Category */}
        {activeCategory && (
          <div className="bg-white border-2 border-[#4A1419] shadow-sm p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b-2 border-[#4A1419]/10 mb-6">
              <div className="flex items-center gap-3">
                <Layers size={22} className="text-[#4A1419]" />
                <h2 className="font-heading text-2xl font-bold text-[#4A1419]">
                  Категория: {activeCategory.category}
                </h2>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDeleteCategory(activeCatIndex)}
                  className="px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 border border-red-200 flex items-center gap-1"
                  title="Удалить всю эту категорию"
                >
                  <Trash2 size={13} />
                  <span>Удалить категорию</span>
                </button>
              </div>
            </div>

            {/* Rows list */}
            <div className="space-y-3">
              {activeCategory.rows && activeCategory.rows.length > 0 ? (
                activeCategory.rows.map((row, rowIdx) => (
                  <div
                    key={rowIdx}
                    className="flex flex-wrap items-center gap-3 p-3 bg-[#FDFBF7] border border-[#4A1419]/20 hover:border-[#4A1419] transition-colors"
                  >
                    <span className="text-xs font-mono font-bold text-[#C5A059] w-6">
                      {rowIdx + 1}.
                    </span>

                    {/* Service Name */}
                    <div className="flex-1 min-w-[260px]">
                      <input
                        type="text"
                        value={row.service}
                        onChange={(e) =>
                          handleServiceChange(activeCatIndex, rowIdx, "service", e.target.value)
                        }
                        placeholder="Название услуги..."
                        className="w-full px-3 py-2 text-sm font-medium border border-transparent hover:border-[#4A1419]/30 focus:border-[#4A1419] bg-transparent focus:bg-white focus:outline-none"
                      />
                    </div>

                    {/* Price */}
                    <div className="w-48">
                      <input
                        type="text"
                        value={row.price}
                        onChange={(e) =>
                          handleServiceChange(activeCatIndex, rowIdx, "price", e.target.value)
                        }
                        placeholder="Цена (например: 2 500 ₽)"
                        className="w-full px-3 py-2 text-sm font-bold font-heading text-[#4A1419] border border-transparent hover:border-[#4A1419]/30 focus:border-[#4A1419] bg-transparent focus:bg-white focus:outline-none text-right"
                      />
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteRow(activeCatIndex, rowIdx)}
                      className="p-2 text-[#6E5549] hover:text-red-700 hover:bg-red-50 transition-colors"
                      title="Удалить услугу"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-sm text-[#6E5549]">
                  В этой категории пока нет услуг. Нажмите кнопку ниже, чтобы добавить первую.
                </div>
              )}
            </div>

            {/* Add Service Row Button */}
            <div className="mt-6 pt-4 border-t border-[#4A1419]/10">
              <button
                onClick={() => handleAddRow(activeCatIndex)}
                className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-[#F5EFE6] text-[#4A1419] border-2 border-dashed border-[#4A1419]/50 hover:bg-[#EFE6D8] flex items-center gap-2"
              >
                <Plus size={15} />
                <span>Добавить услугу в эту категорию</span>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
